/**
 * Live Localhost Preview handler — TASK_PREVIEW_READY / TASK_PREVIEW_CLOSED,
 * plus the sleep-time clear of the ephemeral frame.
 *
 * A ready payload writes the EPHEMERAL per-task `livePreview` field (url/token/
 * port/caption); a closed payload clears it. Ephemeral by construction:
 * `livePreview` is absent from persistence.ts's serialized allowlist, so it never
 * survives a reload — a stale live URL 502s once the sandbox sleeps. Garbage
 * frames are ignored so a malformed relay frame never corrupts task state.
 *
 * THE FRAME'S LIFETIME IS THE SANDBOX BEING AWAKE, NOT THE TURN. It used to be
 * the turn: the server retracted the preview on AGENT_END, and when that moved
 * client-side the web kept dropping the frame the moment the agent stopped
 * talking. Both were the same mistake. A finished turn leaves the dev server
 * running and the hosted url answering, so the card read "Go live" over an app
 * that was already live (prod, 08-26). What actually stops the url answering is
 * the sandbox hibernating (or erroring), which the presence beat already says
 * out loud — so that is what the frame follows.
 *
 * Presence is read through the STORE, not a second socket listener:
 * presenceStore._unsubscribe calls socket.off(PRESENCE_UPDATE) with no handler
 * argument, which would strip every sibling listener on that event along with
 * its own.
 *
 * The durable `livePreviewPointer` is untouched by any of this — asleep, the
 * user still has a way back into their app.
 */
import type { Socket } from "socket.io-client";
import {
  EVENTS,
  type Presence,
  type TaskPreviewReadyPayload,
  type TaskPreviewClosedPayload,
} from "@bash/shared";
import { useTaskStore } from "../taskStore";
import { usePresenceStore } from "../presenceStore";
import { openComputerOnAppAnnounced } from "./browserAutoOpen";

/** Drop every task's ephemeral frame; the durable pointers are left alone. */
function clearEveryEphemeralFrame(): void {
  useTaskStore.getState()._mutateTasks((tasks) => {
    // Only the tasks actually holding a frame are written: a blanket assignment
    // would dirty every immer draft, and most tasks never had a frame at all.
    for (const task of tasks.values()) {
      if (task.livePreview) task.livePreview = undefined;
    }
  });
}

/** The hosted url has stopped answering: errored, upgrading (the box is being
 *  replaced, and a link minted before that answers the platform's own 502, so
 *  the next look mints a proved one), or hibernating with nobody watching an
 *  app (an apps-only wake leaves the phase word at hibernating). */
function asleep(p: Presence): boolean {
  return (
    p.phase === "error" ||
    p.phase === "upgrading" ||
    (p.phase === "hibernating" && p.hasAppActivity !== true)
  );
}

export function attachLivePreviewHandler(socket: Socket): () => void {
  const onReady = (payload: TaskPreviewReadyPayload) => {
    if (
      !payload ||
      typeof payload.taskId !== "string" ||
      typeof payload.url !== "string" ||
      typeof payload.token !== "string" ||
      typeof payload.port !== "number"
    ) {
      return;
    }
    const { taskId, url, token, port, caption, path, tokenParam } = payload;
    const label = typeof caption === "string" ? caption : undefined;
    // Already normalized server-side (announcePreview is the one place that
    // decides what a path is); a type check is all this seam owes it.
    const entry = typeof path === "string" ? path : undefined;
    useTaskStore.getState()._mutateTasks((tasks) => {
      const task = tasks.get(taskId);
      if (!task) return;
      // A boat link names its token's parameter; a Blaxel one carries no key.
      task.livePreview = {
        url,
        token,
        port,
        caption: label,
        path: entry,
        ...(typeof tokenParam === "string" ? { tokenParam } : {}),
      };
      // The announce IS the pointer move server-side; mirror it locally so the
      // App tab survives the turn end without waiting for the next sync.
      task.livePreviewPointer = {
        port,
        caption: label,
        path: entry,
        announced_at: new Date().toISOString(),
      };
    });
    // Put the app in front of the user: open the Computer panel on Apps
    // (manual-close honored inside). A re-announce is the agent's way of
    // opening it again on request, so this runs on EVERY ready, not once.
    openComputerOnAppAnnounced(taskId);
  };

  const onClosed = (payload: TaskPreviewClosedPayload) => {
    if (!payload || typeof payload.taskId !== "string") return;
    // A closed is a takeover of THIS port by another task, a stop, or a
    // retract. With the pool a task may own several apps, so a closed that
    // names a port touches only the frame/pointer on that port; one without
    // a port (a pre-pool server) still clears whatever the task holds.
    const port = payload.port;
    useTaskStore.getState()._mutateTasks((tasks) => {
      const task = tasks.get(payload.taskId);
      if (!task) return;
      if (
        task.livePreview &&
        (port === undefined || task.livePreview.port === port)
      )
        task.livePreview = undefined;
      if (
        task.livePreviewPointer &&
        (port === undefined || task.livePreviewPointer.port === port)
      )
        task.livePreviewPointer = null;
    });
  };

  socket.on(EVENTS.TASK_PREVIEW_READY, onReady);
  socket.on(EVENTS.TASK_PREVIEW_CLOSED, onClosed);

  // One sandbox sleeps for every task, so every task's frame goes with it. Only
  // the MOMENT of falling asleep clears: a repeated hibernating beat must not
  // race an announce that just proved the sandbox is up again. A box serving a
  // watched app is not asleep, whatever the phase word says (the desktop and
  // the Apps tab read the same pair): an apps wake walks warming → hibernating
  // with hasAppActivity, and clearing on that edge dropped the frame the wake
  // had just written whenever its answer beat the presence beat.
  const unsubscribePresence = usePresenceStore.subscribe((state, prev) => {
    if (asleep(state.presence) && !asleep(prev.presence))
      clearEveryEphemeralFrame();
  });

  return () => {
    socket.off(EVENTS.TASK_PREVIEW_READY, onReady);
    socket.off(EVENTS.TASK_PREVIEW_CLOSED, onClosed);
    unsubscribePresence();
  };
}
